import { Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormControlStatus, FormGroup, ValidationErrors } from "@angular/forms";
import { map } from "rxjs";

export interface FormFieldWrapper<T> {
  value: T;
  status: FormControlStatus;
  touched: boolean;
  errors: ValidationErrors | null;
}

export function validateBlank(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeof value === 'string' && value.trim() === '') {
      return { blank: true };
    }

    return null;
  }
}

export function validateYearsLimitFromToday(yearsLimit: number, isMax: boolean = true): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentDate = new Date();
    const inputDate = new Date(control.value);

    const yearsDifference = currentDate.getFullYear() - inputDate.getFullYear();

    if (isMax ? yearsDifference > yearsLimit : yearsDifference < yearsLimit) {
      return { yearsLimitExceeded: { yearsLimit, isMax } };
    }

    return null;
  }
}

export function sanitizeFields(formGroup: FormGroup): void {
  Object.values(formGroup.controls).forEach(control => {
    if (control instanceof FormGroup) {
      sanitizeFields(control);
    } else if (control instanceof FormControl) {
      if (typeof control.value === 'string') {
        control.setValue(control.value.trim());
      }
    }
  });
}

export function watchFormRecursively<T>(formGroup: FormGroup): { group: Signal<FormFieldWrapper<T>>; children: { [key: string]: any } } {
  const formFieldWrappers: {
    group: Signal<FormFieldWrapper<T>>;
    children: { [key: string]: any };
  } = {
    group: watchFormControl(formGroup),
    children: {},
  };

  Object.entries(formGroup.controls).forEach(([key, control]) => {
    formFieldWrappers.children[key] = (control instanceof FormGroup) ? watchFormRecursively(control) : watchFormControl(control);
  });

  return formFieldWrappers;

}

export function watchFormControl<T>(control: AbstractControl<T>): Signal<FormFieldWrapper<T>> {
  return toSignal(
    control.events.pipe(
      map(() => ({
        value: control.value,
        status: control.status,
        touched: control.touched,
        errors: control.errors
      }))
    ),
    {
      initialValue: {
        value: control.value,
        status: control.status,
        touched: control.touched,
        errors: control.errors
      }
    }
  );
}
